// pages/incident/Incident.tsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Table, Modal, Input, Popconfirm, Spin, Flex, message } from "antd";
import {
  useFetchIncidentsQuery,
  useUpdateIncidentMutation,
  useDeleteIncidentMutation,
} from "./incident.service";
import { CheckOutlined, DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux"; // ✅ ADD
import LocationSelector from "../../components/LocationSelector"; // ✅ ADD
import "./style.css";

const { Column } = Table;

export default function Incident() {
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const navigate = useNavigate();
  const user = useSelector((state) => state.app.user); // ✅ ADD

  // ✅ Location state
  const [locationId, setLocationId] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [resolution, setResolution] = useState("");

  // ✅ Set initial location from user
  useEffect(() => {
    const locId = user?.activeLocationId || user?.restaurantId;
    setLocationId(locId);
  }, [user]);

  // ✅ Handle location change
  const handleLocationChange = (newLocationId) => {
    setLocationId(newLocationId);
    refetch(); // ✅ Refetch incidents for new location
  };

  // ✅ Fetch incidents with locationId
  const { 
    data = { data: { incidents: [] } }, 
    isLoading,
    refetch 
  } = useFetchIncidentsQuery(locationId, {
    refetchOnMountOrArgChange: true,
    skip: !locationId,
  });

  const {
    data: { incidents },
  } = data;

  const [
    requestUpdateIncident,
    { isSuccess: updateSuccess, isLoading: updateLoading },
  ] = useUpdateIncidentMutation();
  const [requestDeleteIncident, { isSuccess: deleteSuccess }] =
    useDeleteIncidentMutation();

  const handleCancel = () => {
    setIsModalOpen(false);
    setResolution("");
  };

  const handleOk = () => {
    if (!resolution || resolution.trim().length === 0) {
      message.error("Please enter a resolution");
      return;
    }
    requestUpdateIncident({ 
      id: isModalOpen, 
      data: { resolution },
      locationId // ✅ Pass locationId
    });
  };

  const handleDelete = (id) => {
    requestDeleteIncident({ id, locationId }); // ✅ Pass locationId
  };

  useEffect(() => {
    if (updateSuccess || deleteSuccess) {
      setResolution("");
      setIsModalOpen(false);
      refetch();
    }
  }, [updateSuccess, deleteSuccess]);

  return (
    <Spin spinning={isLoading}>
      <Card
        className="incident-page"
        bordered={false}
        title={
          <Flex align="center" gap="middle" wrap>
            <span>{`${t("Incident")} ${t("List")}`}</span>
            {/* ✅ Add LocationSelector */}
            <LocationSelector onLocationChange={handleLocationChange} />
          </Flex>
        }
        extra={
          <Button 
            type="primary" 
            onClick={() => navigate("/incidents/create")}
            disabled={!locationId}
          >
            {t("Create")}
          </Button>
        }
      >
        <div className="incident-page-container">
          <Table
            dataSource={incidents}
            pagination={{
              current: 1,
              pageSize: 10,
            }}
            locale={{ 
              emptyText: locationId 
                ? t("No incidents found") 
                : t("Please select a location") 
            }}
          >
            <Column
              align="center"
              title={t("INCIDENT")}
              dataIndex={`incident`}
              render={(record) => record[language] || record["default"]}
            />
            <Column
              align="center"
              title={t("NAME OF EMPLOYEE")}
              dataIndex={"employeeName"}
            />
            <Column
              align="center"
              title={t("RESOLUTION")}
              dataIndex={`resolution`}
              render={(record) => record[language] || record["default"]}
            />
            {/* ✅ Optional: Add Location column */}
            <Column
              align="center"
              title={t("Location")}
              dataIndex={"restaurantId"}
              render={(restaurantId) => restaurantId?.name || "N/A"}
            />
            <Column
              title={t("TOOLS")}
              align="center"
              render={(value, record, index) => (
                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    justifyContent: "center",
                  }}
                >
                  <CheckOutlined
                    className={
                      record["resolution"][language] !== "" ||
                      record["resolution"]["default"] !== ""
                        ? "checklist-complete"
                        : "checklist-pending"
                    }
                    onClick={() => {
                      setIsModalOpen(record._id);
                      setResolution(
                        record[`resolution`][language] ||
                          record[`resolution`]["default"]
                      );
                    }}
                  />
                  <Popconfirm
                    title={t("Delete Incident")}
                    description={t(
                      "Are you sure you want to delete this incident?"
                    )}
                    onConfirm={() => handleDelete(record._id)}
                    okText={t("Yes")}
                    cancelText={t("No")}
                  >
                    <DeleteOutlined style={{ color: "red", cursor: "pointer" }} />
                  </Popconfirm>
                </div>
              )}
            />
          </Table>
        </div>
        <Modal
          title={t("RESOLUTION")}
          open={isModalOpen ? true : false}
          onOk={handleOk}
          onCancel={handleCancel}
          confirmLoading={updateLoading}
        >
          <Input.TextArea
            rows={3}
            onChange={(e) => setResolution(e.target.value)}
            value={resolution}
            placeholder={t("Enter resolution details...")}
          />
        </Modal>
      </Card>
    </Spin>
  );
}